import { CommonModule, JsonPipe } from '@angular/common';
import { AsyncPipe, DatePipe } from '@angular/common';
import { HttpParams, httpResource } from '@angular/common/http';
import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Data, Router, RouterModule } from '@angular/router';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { ListComponent } from '@components/list/list.component';
import { PromptComponent } from '@components/prompt/prompt.component';
import { environment } from '@environment';
import { Case, ItemListResponse, Placename } from '@models';
import { ProjectionCode } from '@models/leaflet';
import { LinkPipe, ResolveUserPipe } from '@pipes';
import { AuthService, CaseService, NotificationService, PlacenameService, TitleService } from '@services';
import geojson from 'geojson';

@Component({
  selector: 'app-placename-item',
  imports: [
    JsonPipe,
    DatePipe,
    RouterModule,
    CommonModule,

    MatChipsModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatPaginatorModule,
    MatMenuModule,
    MatDividerModule,
    MatCardModule,
    MatTabsModule,

    ResolveUserPipe,
    AsyncPipe,
    LinkPipe,

    LeafletGroupComponent,
    ListComponent,
  ],
  templateUrl: './item.component.html',
  styleUrl: './item.component.scss'
})
export class PlacenameItemComponent {
  route = inject(ActivatedRoute);
  dialog = inject(MatDialog);
  authService = inject(AuthService);
  placenameService = inject(PlacenameService);
  notificationService = inject(NotificationService);
  caseService = inject(CaseService);
  titleService = inject(TitleService);
  router = inject(Router);

  placename = signal<Placename>(this.route.snapshot.data['placename']);

  casesPage = signal(0);
  cases = httpResource<ItemListResponse<Case>>(() => {
    const httpParams = new HttpParams()
      .set('skip', this.casesPage() * 10)
      .set('take', 10)
      .set('count', true)
      .set('order', 'created:desc')
      .set('placename', this.placename().id);

    return `${environment.kinkoHost}/case/?${httpParams.toString()}`;
  });

  replacesPage = signal(0)
  replaces = httpResource<ItemListResponse<Placename>>(() => {
    const httpParams = new HttpParams()
      .set('skip', this.replacesPage() * 10)
      .set('take', 10)
      .set('count', true)
      .set('order', 'created:desc')
      .set('replacedBy', this.placename().id);

    return `${environment.kinkoHost}/placename/?${httpParams.toString()}`;
  });

  replacedByPage = signal(0)
  replacedBy = httpResource<ItemListResponse<Placename>>(() => {
    const httpParams = new HttpParams()
      .set('skip', this.replacedByPage() * 10)
      .set('take', 10)
      .set('count', true)
      .set('order', 'created:desc')
      .set('replaces', this.placename().id);

    return `${environment.kinkoHost}/placename/?${httpParams.toString()}`;
  });

  alternativePage = signal(0)
  alternative = httpResource<ItemListResponse<Placename>>(() => {
    const httpParams = new HttpParams()
      .set('skip', this.alternativePage() * 10)
      .set('take', 10)
      .set('count', true)
      .set('order', 'created:desc')
      .set('alternative', this.placename().id);

    return `${environment.kinkoHost}/placename/?${httpParams.toString()}`;
  });

  selectedIndex = computed(() => {
    if (this.cases.value()?.items.length) {
      return 0;
    }

    if (this.alternative.value()?.items.length) {
      return 1;
    }

    if (this.replacedBy.value()?.items.length) {
      return 2;
    }

    if (this.replaces.value()?.items.length) {
      return 3;
    }

    return -1;
  });

  geometry = computed(() => {
    const geometry = this.placename().geometry;
    const collection = {
      type: 'FeatureCollection',
      features: [],
    } as geojson.FeatureCollection;

    if (!geometry) {
      return collection;
    }

    let projection = ProjectionCode.Mercator;
    if (geometry.type === "Point") {
      if (!geometry.coordinates?.length) {
        return collection; // corrupt geometry
      }

      const latitude = geometry.coordinates[1];
      if (latitude > 45) {
        projection = ProjectionCode.Arctic;
      } else if (latitude < -60) {
        projection = ProjectionCode.Antarctic;
      }
    }

    collection.features.push(
      {
        type: 'Feature',
        properties: {
          projection: projection,
        },
        geometry: geometry,
      } as geojson.Feature<geojson.Geometry, geojson.GeoJsonProperties>);

    return collection;
  });

  constructor() {
    this.route.data.subscribe((data: Data) => {
      this.placename.set(data["placename"]);
      this.titleService.setTitle(this.placename().title);
    });
  }

  delete() {
    const dialogRef = this.dialog.open(PromptComponent, {
      minWidth: '360px',
      width: '480px',
      data: {
        title: 'Delete placename',
        content:
          'Confirm to delete this placename. This action can not be undone.',
      },
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) {
        return;
      }

      this.placenameService.delete(this.placename().id).subscribe({
        next: () => {
          this.router.navigate(['/placename']).finally(() => {
            this.notificationService.success('Placename deleted successfully.');
          });
        },
        error: (err) => {
          this.notificationService.error('Failed to delete placename.');
          console.error(err);
        }
      });
    });
  }
}
